// Feature flag system for plan-based access control
import { useState, useEffect, createContext, useContext } from 'react';
import axios from './axios';

// Define all available features in the system
export const FEATURES = {
  // Core features (available in all plans)
  PRODUCTS: 'products',
  CLIENTS: 'clients',
  FOURNISSEURS: 'fournisseurs',
  ACHATS: 'achats',
  VENTES: 'ventes',
  DEPENSES: 'depenses',
  STOCK: 'stock',
  
  // E-Commerce Starter features
  MARKETPLACE: 'marketplace',
  ORDERS: 'orders',
  ECOMMERCE: 'ecommerce',
  
  // Business ERP features
  ANALYTICS: 'analytics',
  REPORTS: 'reports',
  FINANCES: 'finances',
  
  // Full Ecosystem features
  POS: 'pos',
  PRODUCTION: 'production',
  LOGISTICS: 'logistics',
  MEMBERS: 'members',
  SETTINGS: 'settings',
  MODE_SWITCHER: 'mode_switcher', // Mode switcher in header (Full Ecosystem only)
};

// Feature flag context
const FeatureFlagContext = createContext({
  allowedFeatures: [],
  hasFeature: () => false,
  isLoading: true,
  planName: 'trial',
});

// Hook to use feature flags
export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within FeatureFlagProvider');
  }
  return context;
};

// Global singleton to prevent multiple concurrent fetches across all provider instances
let globalFetchState = {
  isFetching: false,
  lastFetchTime: 0,
  fetchPromise: null,
  subscribers: new Set(),
};

const CACHE_DURATION = 5 * 60 * 1000; // Cache for 5 minutes
const MIN_FETCH_INTERVAL = 10000; // Minimum 10 seconds between fetches

const getCurrentUsername = () => {
  try {
    return localStorage.getItem('username') || null;
  } catch {
    return null;
  }
};

// Global fetch function that ensures only one request at a time
const globalFetchFeatures = async (force = false) => {
  const now = Date.now();
  
  // If already fetching, return the existing promise
  if (globalFetchState.isFetching && globalFetchState.fetchPromise) {
    return globalFetchState.fetchPromise;
  }
  
  // Check if we recently fetched (unless forced)
  if (!force && (now - globalFetchState.lastFetchTime) < MIN_FETCH_INTERVAL) {
    return Promise.resolve();
  }
  
  // Check cache first
  const cachedData = localStorage.getItem('feature_flags_cache');
  const cacheTime = localStorage.getItem('feature_flags_cache_time');
  let cachedObject = null;
  if (cachedData) {
    try {
      cachedObject = JSON.parse(cachedData);
    } catch {
      cachedObject = null;
    }
  }
  const currentUsername = getCurrentUsername();
  const cacheMatchesUser = !!(cachedObject?.username && currentUsername && cachedObject.username === currentUsername);
  const cacheValid = cachedObject && cacheTime && (now - parseInt(cacheTime, 10)) < CACHE_DURATION && cacheMatchesUser;
  
  if (cacheValid && !force) {
    // Notify all subscribers
    globalFetchState.subscribers.forEach(callback => {
      callback({
        allowed_features: cachedObject.allowed_features || [],
        plan_name: cachedObject.plan_name || 'trial',
        username: cachedObject.username || null,
      });
    });
    return Promise.resolve();
  }
  
  // Set fetching state
  globalFetchState.isFetching = true;
  
  // Create the fetch promise
  globalFetchState.fetchPromise = axios.get('/api/user-plan-features/', {
    validateStatus: function (status) {
      // Allow 200, 401, 403, and 500 through so we can handle them properly
      return status < 600;
    }
  }).then(response => {
    globalFetchState.lastFetchTime = Date.now();
    globalFetchState.isFetching = false;
    globalFetchState.fetchPromise = null;
    
    let result;
    if (response.status === 200) {
      const features = response.data.allowed_features || [];
      const plan = response.data.plan_name || 'trial';
      const apiUsername = response.data?.username || currentUsername || null;
      const apiUserId = response.data?.user_id || null;
      
      // Never default to all features - use the features returned from API
      // If empty, it means no features are allowed (shouldn't happen, but handle gracefully)
      result = {
        allowed_features: features, // Use actual features from API, don't default to all
        plan_name: plan,
        username: apiUsername,
        user_id: apiUserId,
      };
      
      // Cache the result
      localStorage.setItem('feature_flags_cache', JSON.stringify(result));
      localStorage.setItem('feature_flags_cache_time', Date.now().toString());
    } else if (response.status === 500) {
      // CRITICAL: UserPlan is missing - this is a data consistency error
      const errorDetail = response.data?.detail || 'Please contact support.';
      const errorCode = response.data?.error_code || 'USER_PLAN_MISSING';
      const supportPhone = response.data?.support_phone || '+212676263023';
      const supportWhatsapp = response.data?.support_whatsapp || '212676263023';
      
      console.error('❌ CRITICAL: User plan missing!', {
        error: errorDetail,
        errorCode: errorCode,
        user: response.data?.username,
        userId: response.data?.user_id,
        support_phone: supportPhone,
        support_whatsapp: supportWhatsapp
      });
      
      // Show user-friendly error message
      if (typeof window !== 'undefined') {
        // Dispatch a custom event that components can listen to
        window.dispatchEvent(new CustomEvent('userPlanMissing', {
          detail: {
            message: errorDetail,
            errorCode: errorCode,
            support_phone: supportPhone,
            support_whatsapp: supportWhatsapp
          }
        }));
      }
      
      // Use cache if available, otherwise return empty (no features)
      if (cachedObject && cacheMatchesUser) {
        try {
          result = cachedObject;
          console.warn('⚠️  Using cached feature flags due to missing UserPlan');
        } catch (e) {
          result = { allowed_features: [], plan_name: 'error', error: errorDetail };
        }
      } else {
        result = { allowed_features: [], plan_name: 'error', error: errorDetail };
      }
    } else {
      // 401/403 - use cache or return empty (will be handled by provider)
      if (cachedObject && cacheMatchesUser) {
        try {
          result = cachedObject;
        } catch (e) {
          result = { allowed_features: [], plan_name: 'trial' };
        }
      } else {
        result = { allowed_features: [], plan_name: 'trial' };
      }
    }
    
    // Notify all subscribers
    globalFetchState.subscribers.forEach(callback => {
      callback(result);
    });
    
    return result;
  }).catch(error => {
    globalFetchState.isFetching = false;
    globalFetchState.fetchPromise = null;
    
    // On error, use cache if available, otherwise return empty
    let result;
    if (cachedObject && cacheMatchesUser) {
      try {
        result = cachedObject;
      } catch (e) {
        result = { allowed_features: [], plan_name: 'trial' };
      }
    } else {
      result = { allowed_features: [], plan_name: 'trial' };
    }
    
    // Notify all subscribers
    globalFetchState.subscribers.forEach(callback => {
      callback(result);
    });
    
    if (error.response?.status !== 429) {
      console.error('Error fetching feature flags:', error);
    }
    
    return result;
  });
  
  return globalFetchState.fetchPromise;
};

// Feature flag provider component
export const FeatureFlagProvider = ({ children }) => {
  const [allowedFeatures, setAllowedFeatures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [planName, setPlanName] = useState('trial');

  useEffect(() => {
    let mounted = true;
    let debounceTimeout = null;
    
    // Subscribe to global fetch updates
    const updateCallback = (data) => {
      if (!mounted) return;
      setAllowedFeatures(data.allowed_features || []);
      setPlanName(data.plan_name || 'trial');
      setIsLoading(false);
    };
    
    globalFetchState.subscribers.add(updateCallback);
    
    // Debounced fetch function
    const debouncedFetch = (force = false) => {
      if (debounceTimeout) clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => {
        if (mounted) {
          globalFetchFeatures(force);
        }
      }, force ? 100 : 1000); // Immediate if forced, 1 second delay otherwise
    };

    const token = localStorage.getItem('access_token');
    const isAuthenticated = !!token;
    
    // Check cache first and set initial state
    const cachedRaw = localStorage.getItem('feature_flags_cache');
    const cacheTime = localStorage.getItem('feature_flags_cache_time');
    const now = Date.now();
    let cached = null;
    if (cachedRaw) {
      try {
        cached = JSON.parse(cachedRaw);
      } catch {
        cached = null;
      }
    }
    const cacheUsername = cached?.username;
    const currentCachedUser = getCurrentUsername();
    const cacheValid = cached && cacheTime && cacheUsername && currentCachedUser &&
      cacheUsername === currentCachedUser &&
      (now - parseInt(cacheTime, 10)) < CACHE_DURATION;
    
    if (!isAuthenticated) {
      // User not logged in - public state
      setAllowedFeatures([]);
      setPlanName('public');
      setIsLoading(false);
    } else {
      if (cacheValid) {
        setAllowedFeatures(cached.allowed_features || []);
        setPlanName(cached.plan_name || 'trial');
        setIsLoading(false);
      } else {
        // On first load or when cache is invalid, keep loading true
        // This prevents race condition on mobile where component might render
        // before the network request completes
        setIsLoading(true);
        // Don't set empty arrays here - keep previous state until data loads
        // This ensures hasFeature() returns false (safe default) while loading
      }
      // Always revalidate for authenticated sessions
      debouncedFetch(true);
    }
    
    // Listen for login events to refresh features
    const handleLogin = () => {
      localStorage.removeItem('feature_flags_cache');
      localStorage.removeItem('feature_flags_cache_time');
      setIsLoading(true);
      debouncedFetch(true);
    };
    
    // Listen for plan changes
    const handlePlanChange = () => {
      localStorage.removeItem('feature_flags_cache');
      localStorage.removeItem('feature_flags_cache_time');
      debouncedFetch(true);
    };
    
    // Listen for logout events to reset features
    const handleLogout = () => {
      localStorage.removeItem('feature_flags_cache');
      localStorage.removeItem('feature_flags_cache_time');
      globalFetchState.isFetching = false;
      globalFetchState.fetchPromise = null;
      globalFetchState.lastFetchTime = 0;
      setAllowedFeatures([]);
      setPlanName('public');
      setIsLoading(false);
    };
    
    window.addEventListener('userLoggedIn', handleLogin);
    window.addEventListener('planChanged', handlePlanChange);
    window.addEventListener('sessionExpired', handleLogout);
    
    return () => {
      mounted = false;
      if (debounceTimeout) clearTimeout(debounceTimeout);
      globalFetchState.subscribers.delete(updateCallback);
      window.removeEventListener('userLoggedIn', handleLogin);
      window.removeEventListener('planChanged', handlePlanChange);
      window.removeEventListener('sessionExpired', handleLogout);
    };
  }, []); // Only run once on mount

  const hasFeature = (featureKey) => {
    if (isLoading) {
      return false;
    }

    if (!featureKey) {
      return true;
    }

    if (!allowedFeatures || allowedFeatures.length === 0) {
      return false;
    }

    return allowedFeatures.includes(featureKey);
  };

  return (
    <FeatureFlagContext.Provider
      value={{
        allowedFeatures,
        hasFeature,
        isLoading,
        planName,
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
};

// Higher-order component to protect routes/components
export const withFeatureFlag = (Component, requiredFeature) => {
  return (props) => {
    const { hasFeature, isLoading } = useFeatureFlags();
    
    if (isLoading) {
      return <div>Loading...</div>;
    }
    
    if (!hasFeature(requiredFeature)) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Feature Not Available</h2>
          <p>This feature requires a plan upgrade.</p>
        </div>
      );
    }
    
    return <Component {...props} />;
  };
};

// Utility function to check feature access (can be used outside React components)
export const checkFeatureAccess = async (featureKey) => {
  try {
    // Check cache first
    const cachedData = localStorage.getItem('feature_flags_cache');
    const cacheTime = localStorage.getItem('feature_flags_cache_time');
    const now = Date.now();
    const currentUsername = getCurrentUsername();
    let cached = null;
    if (cachedData) {
      try {
        cached = JSON.parse(cachedData);
      } catch {
        cached = null;
      }
    }
    const cacheValid = cached && cacheTime && cached.username && currentUsername &&
      cached.username === currentUsername &&
      (now - parseInt(cacheTime, 10)) < CACHE_DURATION;
    
    if (cacheValid) {
      try {
        const features = cached.allowed_features || [];
        if (features.length === 0) return false;
        return features.includes(featureKey);
      } catch (e) {
        // Cache invalid, fetch fresh
      }
    }
    
    // Use global fetch to avoid duplicate requests
    const result = await globalFetchFeatures(false);
    const features = result?.allowed_features || [];
    
    // If no features specified, deny by default
    if (features.length === 0) {
      return false;
    }
    
    return features.includes(featureKey);
  } catch (error) {
    console.error('Error checking feature access:', error);
    return false;
  }
};

// Allow other modules (e.g., login form) to force-refresh feature flags after auth events
export const forceRefreshFeatureFlags = async () => {
  try {
    localStorage.removeItem('feature_flags_cache');
    localStorage.removeItem('feature_flags_cache_time');
  } catch (e) {
    // Ignore cache clearing errors (e.g., private mode)
  }
  return globalFetchFeatures(true);
};

